import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import some from 'lodash/some';
import size from 'lodash/size';
import moment from 'moment';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  getActiveUnitLOInsights,
  getAssignmentInsights,
  getAssignmentsScoreInsights,
  getAssignmentPerformanceData,
  getCourseLoginData,
  getTimeScoreData,
  getDataNGASummary,
} from '../../../../../core/api';
import { getSortedBarValues } from '../QQInsightsCard/helpers';
import studentLoginNullStateImage from './images/thumbnail_logins.png';
import assignmentSnapshotNullStateImage from './images/thumbnail_assignment_snapshot.png';
import assignmentLONullStateImage from './images/thumbnail_assignments_learning_objectives.png';
import assignmentToTNullStateImage from './images/thumbnail_time_on_task.png';
import completedAssignmentNullStateImage from './images/thumbnail_completed_assignment.png';
import srlConfidenceNullStateImage from './images/thumbnail_srl_confidence.png';
import srlGoalsNullStateImage from './images/thumbnail_srl_goals.png';
import srlHoursNullStateImage from './images/thumbnail_srl_hours.png';
import srlGrowthNullStateImage from './images/thumbnail_srl_growth.png';
import srlChallengesImage from './images/thumbnail_srl_challenges.png';
import srlAcademicImage from './images/thumbnail_srl_academics.png';
import srlSpacingPacingImage from './images/thumbnail_srl_spacing_pacing.png';
import srlChallengeAndRecallImage from './images/thumbnail_srl_challenge_and_recall.png';
import srlHelpAndCollaborationImage from './images/thumbnail_srl_help_and_collaboration.png';
import srlEnvironmentAndMindsetImage from './images/thumbnail_srl_environment_and_mindset.png';

import {
  getNullStateMessage,
  isAnEmptySRLWidget,
  nullStateMessages,
} from '../../nullStateMessages';
import {
  WIDGET_TYPE_TOP_LO_BAR_CHART,
  WIDGET_TYPE_TOP_ASSIGNMENTS_BAR_CHART,
  WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART,
  WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART,
  WIDGET_TYPE_LOGIN_PIE_CHART,
  WIDGET_TYPE_TIME_SCORE_CHART,
  WIDGET_TYPE_TIME_ATTEMPTS_CHART,
  WIDGET_TYPE_COMPLETED_ASSIGNMENT,
  WIDGET_TYPE_SRL_CONFIDENCE_CHART,
  WIDGET_TYPE_SRL_GOAL_CHART,
  WIDGET_TYPE_SRL_HOURS_CHART,
  WIDGET_TYPE_SRL_GROWTH_CHART,
  WIDGET_TYPE_SRL_CHALLENGES_CHART,
  WIDGET_TYPE_SRL_ACADEMICS_CHART,
  WIDGET_TYPE_SRL_SPACING_PACING_CHART,
  WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART,
  WIDGET_TYPE_SRL_HELP_AND_COLLABORATION,
  WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET,
  LOGIN_SLICE_NAMES,
  isSRLWidget,
  isAssignmentPerfWidget,
} from '../../../../constants';
import {
  REPORT_TYPE_INSIGHTS_LO,
  REPORT_TYPE_INSIGHTS_ASSIGNMENT,
  REPORT_TYPE_INSIGHTS_EXTERNAL_LO,
  REPORT_TYPE_ASSIGNMENT_SCORE,
  REPORT_TYPE_LOGIN_INSIGHTS,
  REPORT_TYPE_TIME_SCORE,
  REPORT_TYPE_TIME_ATTEMPTS,
  REPORT_TYPE_COMPLETED_ASSIGNMENT,
} from '../../../../../actions/constants';

import { getFormattedAverage, getFormattedDate, getFormattedTimeSpent } from '../../../../utils';
import { getSRLSurveyResultsByCard as getSRLSurveyResultsByCardAction } from '../../../../../actions/insights';


import QQInsightDetailsModal from '../QQInsightDetailsModal';
import QQInsightsCard from '../QQInsightsCard';

function getInsightAssignmentSubtitle(minRangeDays, maxRangeDays, allTimeText = 'All assignments') {
  if (Number.isInteger(minRangeDays) && Number.isInteger(maxRangeDays)) {
    const startRange = moment().subtract(minRangeDays, 'days').format('dddd, MMMM D');
    const endRange = moment().add(maxRangeDays, 'days').format('dddd, MMMM D');
    return `${startRange} - ${endRange}`;
  }
  return allTimeText;
}

const loNullStateMessage = (activeUnit, learningObjectives, notStarted, hasStudents, onTabChange) => {
  const hasLearningObjectives = Array.isArray(learningObjectives) && learningObjectives.length;
  const hasQuestions = Array.isArray(learningObjectives) && learningObjectives.reduce((prev, current) => current.numQuestions + prev, 0) > 0;
  if (!hasQuestions) return nullStateMessages.noDataMessage();
  return getNullStateMessage(onTabChange, activeUnit, notStarted, hasStudents, hasLearningObjectives);
};

const getAssignmentInsightsNullStateMessage = (assignments, onTabChange, limit) => {
  const hasAssignmentInsights = Array.isArray(assignments) && assignments.length;
  const thereIsData = Array.isArray(assignments) && assignments.slice(0, limit).reduce((prev, current) => current.numQuestions + prev, 0) > 0;
  if (!hasAssignmentInsights || !thereIsData) return nullStateMessages.noDataMessage();
  return false;
};


const getExternalInsightsNullStateMessage = (externalInsightsData) => {
  const hasResponses = some(
    externalInsightsData,
    b => get(b, 'averageStarted') !== 0
  );
  const hasQuestions = externalInsightsData.some(item => get(item, 'numQuestions') > 0);

  const isPublished = size(externalInsightsData) > 0;
  if (!isPublished && !hasResponses) {
    return nullStateMessages.externalFrameworkHasNoResponses();
  }
  if (!hasQuestions) {
    return nullStateMessages.noDataMessage();
  }
  return false;
};

const getAssignmentsDataNullStateMessage = (assignmentIds, externalInsightsData) => {
  const hasData = size(externalInsightsData) > 0 && externalInsightsData.average > 0;
  return (assignmentIds.length === 0 || !hasData) && nullStateMessages.noScoresForAssignment();
};

const getLoginDataNullStateMessage = (externalInsightsData) => {
  const studentsHaveLoggedInInsights = find(externalInsightsData, { name: LOGIN_SLICE_NAMES.LOGGED_IN });
  const studentsHaveNotLoggedInInsights = find(externalInsightsData, { name: LOGIN_SLICE_NAMES.NOT_LOGGED_IN });
  const studentsHaveLoggedIn = studentsHaveLoggedInInsights && studentsHaveLoggedInInsights.value;
  const studentsHaveNotLoggedIn = studentsHaveNotLoggedInInsights && studentsHaveNotLoggedInInsights.value;
  const totalStudents = studentsHaveLoggedIn + studentsHaveNotLoggedIn;
  const hasData = size(externalInsightsData) > 0 && totalStudents > 0;
  return !hasData && nullStateMessages.noDataForLogin();
};

const getTimeScoreDataNullStateMessage = (assignmentIds, externalInsightsData) => {
  const hasData = size(externalInsightsData) > 0;
  return (assignmentIds.length === 0 || !hasData) && nullStateMessages.noSelectedAssignment();
};

const getSRLNullStateMessage = (srlData, widgetType, isSRLIntroSurvey) => isAnEmptySRLWidget(srlData, widgetType, isSRLIntroSurvey) && nullStateMessages.noSelectedAssignment();

const getWidgetData = async (courseId, widgetType, dashboardWidgetId, limit, minRangeDays, maxRangeDays, assignmentIds, assignmentList) => {
  let queryParams = queryString.stringify({ minRangeDays, maxRangeDays });
  let response;
  if (widgetType === WIDGET_TYPE_TOP_LO_BAR_CHART) {
    response = await getActiveUnitLOInsights(courseId, dashboardWidgetId, queryParams);
    if (!response.error) {
      const learningObjectives = get(response, 'activeUnit.learningObjectives', []);
      response.activeUnit = get(response, 'activeUnit');
      response.notStarted = get(response, 'notStarted');
      response.insightsData = getSortedBarValues(learningObjectives, limit);
      response.subTitle = getInsightAssignmentSubtitle(minRangeDays, maxRangeDays, 'All Time');
      response.lastFetchedAt = get(response, 'lastFetchedAt');
    }
  } else if (widgetType === WIDGET_TYPE_TOP_ASSIGNMENTS_BAR_CHART) {
    response = await getAssignmentInsights(courseId, dashboardWidgetId, queryParams);
    if (!response.error) {
      const assignments = get(response, 'assignments', []);
      response.insightsData = getSortedBarValues(assignments, limit);
      response.subTitle = getInsightAssignmentSubtitle(minRangeDays, maxRangeDays);
      response.lastFetchedAt = get(response, 'lastFetchedAt');
    }
  } else if (widgetType === WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART) {
    response = await getActiveUnitLOInsights(courseId, dashboardWidgetId, queryParams);
    if (!response.error) {
      const externalInsightsData = get(response, 'activeUnit.externalFrameworkBranches', []);
      const frameworkName = get(externalInsightsData, '[0].externalFrameworkName', 'external framework');
      response.activeUnit = get(response, 'activeUnit');
      response.notStarted = get(response, 'notStarted');
      response.insightsData = getSortedBarValues(externalInsightsData, limit);
      response.title = `What are the top ${frameworkName} levels to review?`;
      response.subTitle = getInsightAssignmentSubtitle(minRangeDays, maxRangeDays, 'All Time');
      response.lastFetchedAt = get(response, 'lastFetchedAt');
    }
  } else if (widgetType === WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART) {
    if (assignmentIds.length > 0) {
      queryParams = queryString.stringify({ assignmentIds: assignmentIds.join(',') });
      const scoreData = await getAssignmentsScoreInsights(courseId, queryParams);
      const formatedData = [];
      let averageRange = '';
      scoreData.assignmentScoreSnapshot.forEach((as) => {
        const rangeText = `${as.rangeLow}-${as.rangeHigh - (as.rangeHigh === 100 ? 0 : 1)}%`;
        formatedData.push({ Score: rangeText, Students: as.numStudents });
        if (scoreData.average >= as.rangeLow && scoreData.average < as.rangeHigh) {
          averageRange = rangeText;
        }
      });
      response = {};
      response.insightsData = { assignmentsScore: formatedData, average: scoreData.average, averageRange };
      response.subTitle = scoreData.subTitle;
    }
  } else if (widgetType === WIDGET_TYPE_LOGIN_PIE_CHART) {
    const loginData = await getCourseLoginData(courseId, queryParams);

    if (!loginData.error) {
      response = {
        insightsData: [
          { value: loginData.loginData, name: LOGIN_SLICE_NAMES.LOGIN_DATA },
          { value: loginData.numStudentsEnrolled - loginData.numUsersLoggedIn, name: LOGIN_SLICE_NAMES.NOT_LOGGED_IN },
          { value: loginData.numUsersLoggedIn, name: LOGIN_SLICE_NAMES.LOGGED_IN },
        ],
      };
      response.title = 'How many of my students aren’t logging in?';
      response.subTitle = getInsightAssignmentSubtitle(minRangeDays, maxRangeDays, 'All Time');
    }
  } else if (widgetType === WIDGET_TYPE_TIME_SCORE_CHART || widgetType === WIDGET_TYPE_TIME_ATTEMPTS_CHART) {
    if (assignmentIds.length > 0) {
      const assignmentId = assignmentIds[0];
      const assignment = assignmentList.find(a => a.value === assignmentId);
      const timeScoreData = await getTimeScoreData(courseId, assignment.tool, assignmentId);
      const formattedData = [];
      let averageTime = 0;
      let totalTime = 0;
      let totalAttempts = 0;
      let averageScore = 0;
      let totalScore = 0;
      let averageAttempts = 0;
      if (timeScoreData && timeScoreData.length > 0) {
        timeScoreData.forEach((data) => {
          const newData = {
            grade: data.grade * 100,
            time: data.timeOnAssignmentInMins,
            name: data.name,
            attempts: data.questionsAttempted,
          };
          totalTime += data.timeOnAssignmentInMins;
          totalScore += (data.grade * 100);
          totalAttempts += data.questionsAttempted;
          formattedData.push(newData);
        });
        averageTime = totalTime / timeScoreData.length;
        averageScore = totalScore / timeScoreData.length;
        averageAttempts = totalAttempts / timeScoreData.length;
      }
      response = { insightsData: formattedData, averageTime, averageScore, averageAttempts };
    }
  } else if (widgetType === WIDGET_TYPE_COMPLETED_ASSIGNMENT) {
    if (assignmentIds.length > 0) {
      const assignmentId = assignmentIds[0];

      const ngaSummaryData = await getDataNGASummary(courseId, assignmentId);
      const completionStats = get(ngaSummaryData, 'completionStats', {});

      if (completionStats && Object.keys(completionStats).length > 0) {
        const { didNotOpen, inProgress, isComplete, openNotStarted } = completionStats;

        response = {
          insightsData: [completionStats],
          didNotOpen: didNotOpen || 0,
          inProgress: inProgress || 0,
          openNotStarted: openNotStarted || 0,
          completed: isComplete || 0,
        };
      }
    }
  }
  return response;
};

const getAssignmentPerformanceModalData = async (courseId, assignmentId) => {
  const queryParams = {};
  const stringifiedQueryParams = queryString.stringify(queryParams);
  const response = await getAssignmentPerformanceData(
    courseId,
    assignmentId,
    stringifiedQueryParams
  );
  const ngaAssignmentData = response.results;
  const averageTime = getFormattedTimeSpent(response.timeSpentAvg);
  const averageScore = getFormattedAverage(response.gradeAvg, 0, true);
  const studentData = [
    ["Student", "Score", "Time Spent", "First Activity", "Last Activity"],
  ];
  if (ngaAssignmentData && ngaAssignmentData.length > 0) {
    ngaAssignmentData.forEach((data) => {
      const summaryData = [];
      summaryData[0] = {
        first_name: data.firstName,
        last_name: data.lastName,
        studentId: data.userId,
        studentEmail: data.email,
      };
      summaryData[1] = Number.isFinite(data.grade)
        ? `${Number.parseFloat(data.grade * 100).toFixed(0)}%`
        : "0%";
      summaryData[2] = getFormattedTimeSpent(data.timeOnAssignmentInMins);
      summaryData[3] = getFormattedDate(data.firstActivity);
      summaryData[4] = getFormattedDate(data.lastActivity);
      studentData.push(summaryData);
    });
  }
  const assignmentPerfData = { studentData, averageScore, averageTime };

  return assignmentPerfData;
};

let WidgetWrapper = ({
  widget,
  isDetailView,
  courseId,
  courseShortId,
  productModelId,
  activityId,
  tool,
  hasStudents,
  onTabChange,
  toggleInsightDetailsModal,
  isInsightDetailModalVisible,
  sendAnalyticsEvent,
  limit,
  getSRLSurveyResultsByCard,
  assignments,
  isSRLIntroSurvey,
  srlSurveyResultsByCardData,
  isSRLEnglishVersion,
  isSRLFirstCheckpoint,
}) => {
  const [data, setData] = useState({ subTitle: '', activityId: '', tool: '', insightsData: [], lastFetchedAt: {}, assignmentData: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');

  const widgetData = get(widget, 'widget', {});
  const widgetType = get(widgetData, 'widgetType');
  const dashboardWidgetId = get(widgetData, 'dashboardWidgetId');
  const minRangeDays = get(widgetData, 'minRangeDays');
  const maxRangeDays = get(widgetData, 'maxRangeDays');
  const assignmentIds = get(widgetData, 'assignmentIds', []);
  const assignmentId = get(widgetData, 'assignmentIds[0]', null);

  // SRL Specific Data
  const srlCardId = get(widgetData, 'srlCardId');
  const srlTitle = get(widgetData, 'card_title');
  useEffect(() => {
    if (srlTitle) {
       setTitle(srlTitle);
    }
  }, [srlTitle]);

  let iconType = '';
  let reportType = '';
  let nullStateMessage = '';
  let nullStateImage = '';
  let showCard = true;


  switch (widgetType) {
    case WIDGET_TYPE_TOP_LO_BAR_CHART:
      iconType = 'learning_objective';
      nullStateMessage = loNullStateMessage(data.activeUnit, data.insightsData, data.notStarted, hasStudents, onTabChange);
      nullStateImage = assignmentLONullStateImage;
      reportType = REPORT_TYPE_INSIGHTS_LO;
      break;
    case WIDGET_TYPE_TOP_ASSIGNMENTS_BAR_CHART:
      iconType = 'assignment';
      nullStateMessage = getAssignmentInsightsNullStateMessage(data.insightsData, onTabChange, limit);
      nullStateImage = assignmentLONullStateImage;
      reportType = REPORT_TYPE_INSIGHTS_ASSIGNMENT;
      break;
    case WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART:
      iconType = 'book';
      nullStateMessage = getExternalInsightsNullStateMessage(data.insightsData);
      nullStateImage = assignmentLONullStateImage;
      reportType = REPORT_TYPE_INSIGHTS_EXTERNAL_LO;
      showCard = !data.noExternalFrameworkData;
      break;
    case WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART:
      iconType = 'book';
      nullStateMessage = getAssignmentsDataNullStateMessage(assignmentIds, data.insightsData);
      nullStateImage = assignmentSnapshotNullStateImage;
      reportType = REPORT_TYPE_ASSIGNMENT_SCORE;
      break;
    case WIDGET_TYPE_LOGIN_PIE_CHART:
      iconType = 'person';
      nullStateMessage = getLoginDataNullStateMessage(data.insightsData);
      nullStateImage = studentLoginNullStateImage;
      reportType = REPORT_TYPE_LOGIN_INSIGHTS;
      break;
    case WIDGET_TYPE_TIME_SCORE_CHART:
      iconType = 'clock';
      reportType = REPORT_TYPE_TIME_SCORE;
      nullStateMessage = getTimeScoreDataNullStateMessage(assignmentIds, data.insightsData);
      nullStateImage = assignmentToTNullStateImage;
      nullStateImage = assignmentToTNullStateImage;
      break;
    case WIDGET_TYPE_TIME_ATTEMPTS_CHART:
      iconType = 'clock';
      reportType = REPORT_TYPE_TIME_ATTEMPTS;
      nullStateMessage = getTimeScoreDataNullStateMessage(assignmentIds, data.insightsData);
      nullStateImage = assignmentToTNullStateImage;
      break;
    case WIDGET_TYPE_COMPLETED_ASSIGNMENT:
      iconType = 'check_circle';
      reportType = REPORT_TYPE_COMPLETED_ASSIGNMENT;
      nullStateMessage = getTimeScoreDataNullStateMessage(assignmentIds, data.insightsData);
      nullStateImage = completedAssignmentNullStateImage;
      break;
    case WIDGET_TYPE_SRL_CONFIDENCE_CHART:
      iconType = 'star';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlConfidenceNullStateImage;
      break;
    case WIDGET_TYPE_SRL_GOAL_CHART:
      iconType = 'trophy';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlGoalsNullStateImage;
      break;
    case WIDGET_TYPE_SRL_HOURS_CHART:
      iconType = 'calendar';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlHoursNullStateImage;
      break;
    case WIDGET_TYPE_SRL_GROWTH_CHART:
      iconType = 'flag';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlGrowthNullStateImage;
      break;
    case WIDGET_TYPE_SRL_CHALLENGES_CHART:
      iconType = 'thumbs_down';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlChallengesImage;
      break;
    case WIDGET_TYPE_SRL_ACADEMICS_CHART:
      iconType = 'mortar_board';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlAcademicImage;
      break;
    case WIDGET_TYPE_SRL_SPACING_PACING_CHART:
      iconType = 'crossed_arrows';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlSpacingPacingImage;
      break;
    case WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART:
      iconType = 'stack_checked';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlChallengeAndRecallImage;
      break;
    case WIDGET_TYPE_SRL_HELP_AND_COLLABORATION:
      iconType = 'person_group';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlHelpAndCollaborationImage;
      break;
    case WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET:
      iconType = 'head';
      nullStateMessage = getSRLNullStateMessage(data, widgetType, isSRLIntroSurvey);
      nullStateImage = srlEnvironmentAndMindsetImage;
      break;
    default:
      break;
  }

  const getInsightData = insightId => assignments.find(a => a.value === insightId);

  const getDueDateString = dueDate => `Due ${moment(dueDate).format('ddd, MMMM DD')}`;


  useEffect(() => {
    const fetchData = async () => {
      let _widgetData = null;
      if (courseId && !isLoading) {
        setIsLoading(true);
        if (isSRLWidget(widgetType)) {
          if (srlCardId) {
              // Only fetch data if we didn't already get it
              if (srlSurveyResultsByCardData[`${assignmentId}-${srlCardId}`]) {
                _widgetData = srlSurveyResultsByCardData[`${assignmentId}-${srlCardId}`];
              } else {
                _widgetData = await getSRLSurveyResultsByCard(courseId, assignmentId, srlCardId);
              }

            const assignment = getInsightData(assignmentId);
            _widgetData = { insightsData: _widgetData.components };
            _widgetData.subTitle = assignment.label;
            _widgetData.subTitle2 = <span> {getDueDateString(assignment.assignment_due_at)}</span>;
            _widgetData.isSRLIntroSurvey = isSRLIntroSurvey;
            _widgetData.isSRLEnglishVersion = isSRLEnglishVersion;
            _widgetData.isSRLFirstCheckpoint = isSRLFirstCheckpoint;
          }
          setIsLoading(false);
        } else {
          setTitle(get(widgetData, 'title', ''));
          _widgetData = await getWidgetData(courseId, widgetType, dashboardWidgetId, limit, minRangeDays, maxRangeDays, assignmentIds, assignments);
          // as we don't really know the title for external framework until we get fetch the data
          // the generic title that is automatically created from widget library comes empty
          if (widgetType === WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART) {
            const hasExternalFrameworkData = get(_widgetData, 'activeUnit.externalFrameworkBranches');
            _widgetData.noExternalFrameworkData = hasExternalFrameworkData && hasExternalFrameworkData.length === 0;
          }
          if (widgetType === WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART || widgetType === WIDGET_TYPE_TIME_SCORE_CHART || widgetType === WIDGET_TYPE_TIME_ATTEMPTS_CHART || widgetType === WIDGET_TYPE_COMPLETED_ASSIGNMENT) {
            if (_widgetData) {
              const assignment = getInsightData(assignmentId);
              if ([WIDGET_TYPE_TIME_SCORE_CHART, WIDGET_TYPE_TIME_ATTEMPTS_CHART].includes(widgetType)) {
                _widgetData.subTitle = `Students that completed ${assignment.label}`;
              } else {
                _widgetData.subTitle = assignment.label;
              }
              if (assignmentId && isAssignmentPerfWidget(widgetType)) {
                _widgetData.assignmentData = await getAssignmentPerformanceModalData(courseId, assignmentId);
              }
              _widgetData.subTitle2 = <span> {getDueDateString(assignment.assignment_due_at)}</span>;
              _widgetData.activityId = assignment.activityId;
              _widgetData.tool = assignment.tool;
            }

          }
        }
        setIsLoading(false);
        if (_widgetData && !_widgetData.error) {
          setData(_widgetData);
        } else if (_widgetData) {
          throw new Error();
        }
      }
    };
    fetchData();
  }, [minRangeDays, maxRangeDays, assignmentId]);

  const getTitle = () => {
    if (title.trim() === '') return data.title;
    return title;
  };

  const insightsCard = () => (
    <QQInsightsCard
      data={data}
      isLoading={isLoading}
      iconType={iconType}
      title={getTitle()}
      subTitle={data.subTitle}
      subTitle2={data.subTitle2}
      widget={widgetData}
      courseId={courseId}
      courseShortId={courseShortId}
      productModelId={productModelId}
      assignmentId={assignmentId}
      activityId={activityId || data.activityId}
      tool={tool || data.tool}
      isDetailView={isDetailView}
      hasStudents={hasStudents}
      onTabChange={onTabChange}
      nullStateMessage={nullStateMessage}
      nullStateImage={nullStateImage}
      toggleInsightDetailsModal={toggleInsightDetailsModal}
      sendAnalyticsEvent={sendAnalyticsEvent}
      reportType={reportType}
      limit={limit}
      assignmentList={assignments}
    />
  );

  if (!showCard) {
    return null;
  }

  return (
    <Fragment>
      {isDetailView ? (
        <QQInsightDetailsModal
          title={getTitle()}
          subTitle={data.subTitle}
          iconType={iconType}
          activeUnit={data.activeUnit}
          onTabChange={onTabChange}
          lastFetchedAt={data.lastFetchedAt}
          isInsightDetailModalVisible={isInsightDetailModalVisible}
          toggleInsightDetailsModal={toggleInsightDetailsModal}
          sendAnalyticsEvent={sendAnalyticsEvent}
          reportType={reportType}
        >
          {insightsCard()}
        </QQInsightDetailsModal>
      ) : (
          insightsCard()
        )}
    </Fragment>
  );
};

WidgetWrapper.defaultProps = {
  isModal: false,
};

WidgetWrapper.propTypes = {
  widget: PropTypes.object,
  isDetailView: PropTypes.bool,
  courseId: PropTypes.string,
  courseShortId: PropTypes.string,
  productModelId: PropTypes.number,
  activityId: PropTypes.string,
  tool: PropTypes.string,
  iconType: PropTypes.string,
  nullStateMessage: PropTypes.node,
  toggleInsightDetailsModal: PropTypes.func.isRequired,
  isInsightDetailModalVisible: PropTypes.bool,
  sendAnalyticsEvent: PropTypes.func,
  reportType: PropTypes.string,
  limit: PropTypes.number,
  hasStudents: PropTypes.bool,
  onTabChange: PropTypes.func,
  getSRLSurveyResultsByCard: PropTypes.func,
  assignments: PropTypes.array,
  isSRLIntroSurvey: PropTypes.bool,
  srlSurveyResultsByCardData: PropTypes.object,
  isSRLEnglishVersion: PropTypes.bool,
  isSRLFirstCheckpoint: PropTypes.bool,
};

function mapStateToProps(state) {
  const assignments = get(state, 'insights.dashboard.assignments', []);
  const srlSurveyResultsByCardData = get(state, 'insights.surveys.srlSurveyResultsByCardData', {});
  return {
    assignments,
    srlSurveyResultsByCardData,
  };
}

const mapDispatchToProps = dispatch => bindActionCreators({ getSRLSurveyResultsByCard: getSRLSurveyResultsByCardAction }, dispatch);

WidgetWrapper = connect(mapStateToProps, mapDispatchToProps)(WidgetWrapper);

const MemoizedWidgetWrapper = React.memo(WidgetWrapper, isEqual);

export default MemoizedWidgetWrapper;
